import * as React from "react"
import { useState } from "react"
import {
    Badge,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    Popover,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material"
import FilterListIcon from "@mui/icons-material/FilterList"

import { qualityFlags } from "../@signals"

//: The fields worth asking "did the scraper bring this?" about, grouped the way
//: a scraper is debugged. Keys are the backend's COVERAGE_FIELDS keys, so a
//: filter and the matching column percentage can never disagree.
//:
//: All 33 of them are here, on purpose: a field measured in a column header but
//: missing from this panel reads as a percentage you cannot act on. RC images,
//: RC videos, gifts and six others were missing and were exactly that.
const GROUPS = [
    {
        title: 'Product page',
        fields: [
            { key: 'description', label: 'Description' },
            { key: 'richContent', label: 'Rich content' },
            { key: 'richContentText', label: 'Rich content text' },
            { key: 'numRichContentImages', label: 'RC images' },
            { key: 'numRichContentVideos', label: 'RC videos' },
            { key: 'numImages', label: 'Images' },
            { key: 'numVideos', label: 'Videos' },
            { key: 'eyecatchs', label: 'Eyecatchs' },
            { key: 'gifts', label: 'Gifts' },
            { key: 'giftImages', label: 'Gift images' },
        ],
    },
    {
        title: 'Identity',
        fields: [
            { key: 'brandRaw', label: 'Raw brand' },
            { key: 'productTitle', label: 'Title' },
            { key: 'supertitle', label: 'Supertitle' },
            { key: 'subtitle', label: 'Subtitle' },
            { key: 'variantRaw', label: 'Variant raw' },
            { key: 'ean', label: 'EAN' },
            { key: 'reference', label: 'Reference' },
            { key: 'thumbnail', label: 'Thumbnail' },
            { key: 'productUrl', label: 'Product URL' },
        ],
    },
    {
        title: 'Pricing',
        fields: [
            { key: 'actualPrice', label: 'Actual price' },
            { key: 'listPrice', label: 'List price' },
            { key: 'shippingPrice', label: 'Shipping price' },
            { key: 'currency', label: 'Currency' },
            { key: 'stockOnline', label: 'In stock' },
            { key: 'clickAndCollect', label: 'Click & collect' },
            { key: 'freeShipping', label: 'Free shipping' },
            { key: 'externalSeller', label: 'External seller' },
            { key: 'sellerName', label: 'Seller name' },
        ],
    },
    {
        title: 'Reviews',
        fields: [
            { key: 'rating', label: 'Rating' },
            { key: 'numOpinions', label: 'Opinions' },
            { key: 'lastReviewRating', label: 'Last review rating' },
            { key: 'lastReviewTitle', label: 'Last review title' },
            { key: 'lastReviewDate', label: 'Last review date' },
        ],
    },
]


const countFlags = (flags) => Object.values(flags).filter(v => v !== undefined && v !== null).length

//: Order-independent, so reordering the same choices does not read as a change.
const signature = (flags) => Object.entries(flags)
    .filter(([, value]) => value !== undefined && value !== null)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join('|')


/**
 * One tri-state row: nothing selected = do not filter, Yes = has it, No = lacks it.
 *
 * Labelled buttons rather than two checkboxes: a red tick read as "ticked, so
 * yes" no matter what colour it was. Clicking the active one again clears the
 * field back to "any", which is how a filter stops filtering.
 */
function FlagRow({ field, value, onChange }) {
    const current = value === true ? 'yes' : value === false ? 'no' : null

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            <Typography
                variant="caption"
                title={field.label}
                sx={{
                    flex: 1, color: current ? '#111827' : '#6b7280',
                    fontWeight: current ? 600 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
            >
                {field.label}
            </Typography>

            <ToggleButtonGroup
                exclusive
                size="small"
                value={current}
                onChange={(_event, next) => onChange(next === null ? undefined : next === 'yes')}
                sx={{
                    '& .MuiToggleButton-root': {
                        px: 1, py: 0.1, fontSize: 10, lineHeight: 1.6,
                        border: '1px solid #e5e7eb', color: '#9ca3af',
                    },
                    '& .MuiToggleButton-root.Mui-selected': { fontWeight: 700 },
                    '& .MuiToggleButton-root[value="yes"].Mui-selected': {
                        color: '#15803d', backgroundColor: '#f0fdf4', borderColor: '#bbf7d0',
                        '&:hover': { backgroundColor: '#dcfce7' },
                    },
                    '& .MuiToggleButton-root[value="no"].Mui-selected': {
                        color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca',
                        '&:hover': { backgroundColor: '#fee2e2' },
                    },
                }}
            >
                <ToggleButton value="yes" title={`${field.label}: the scraper brought it`}>
                    Yes
                </ToggleButton>
                <ToggleButton value="no" title={`${field.label}: missing`}>
                    No
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    )
}


/**
 * The "has X?" filters, applied server-side over the whole result.
 *
 * They live in the filter bar rather than behind the table's funnel icon on
 * purpose: everything this screen filters is applied by the backend, and a
 * second panel that looked like the grid's own would suggest it filters the
 * page. It does not — it re-queries, and the coverage percentages move with it.
 */
export default function ScrapingDataReviewQualityFilters() {
    const [anchor, setAnchor] = useState(null)
    // Edited here, committed on Apply. Each committed change re-queries the
    // backend (~1 s), so setting five fields used to mean five round trips and
    // five tables you did not ask for.
    const [draft, setDraft] = useState({})
    const flags = qualityFlags.value || {}
    const active = countFlags(flags)
    const pending = signature(draft) !== signature(flags)

    const openPanel = (event) => {
        setDraft(qualityFlags.value || {})
        setAnchor(event.currentTarget)
    }

    const setFlag = (key, value) => {
        setDraft(current => {
            const next = { ...current }
            if (value === undefined) delete next[key]
            else next[key] = value
            return next
        })
    }

    const apply = () => {
        qualityFlags.value = draft
        setAnchor(null)
    }

    return (
        <>
            {/* An icon, not a labelled button: it lives among the grid's own
                toolbar icons, where a 150px button broke the row.
                MUI's own FilterList icon in a default-size IconButton — the
                very icon and box mui-datatables uses for its toolbar buttons.
                A FontAwesome glyph at a hand-picked size matched neither the
                weight nor the baseline of the two next to it.
                The Badge wraps the ICON, inside the button: wrapping the button
                instead put a different kind of box in a row of IconButtons and
                it never sat at the same height, and the counter hanging off the
                last button's corner was clipped by the card's edge. */}
            <IconButton
                title="Quality filters — did the scraper bring each field?"
                onClick={openPanel}
                sx={{ color: '#6b7280' }}
            >
                <Badge
                    badgeContent={active}
                    color="primary"
                    sx={{
                        '& .MuiBadge-badge': {
                            top: -2, right: -2, height: 14, minWidth: 14, fontSize: 9, padding: '0 3px',
                        },
                    }}
                >
                    {/* Inline, not sx: the theme gives MuiSvgIcon its own
                        `color`, and neither the button's colour nor an sx on
                        the icon beat it — the active state stayed grey.
                        #6b7280 is that theme grey, for parity with the icons
                        beside it; #4a90d9 is the primary the badge uses. */}
                    <FilterListIcon style={{ color: active > 0 ? '#4a90d9' : '#6b7280' }} />
                </Badge>
            </IconButton>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, width: 560 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            Did the scraper bring it?
                        </Typography>
                        {/* Text, and explicitly so: the theme's default Button
                            renders filled, which made Reset look like the
                            primary action next to Apply. */}
                        {countFlags(draft) > 0 && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setDraft({})}
                                sx={{ color: '#6b7280' }}
                            >
                                Reset
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="contained"
                            onClick={apply}
                            disabled={!pending}
                            sx={{ ml: 1 }}
                        >
                            Apply
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label="Yes"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#15803d', backgroundColor: '#f0fdf4' }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            the scraper brought it
                        </Typography>
                        <Chip
                            size="small"
                            label="No"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#b91c1c', backgroundColor: '#fef2f2', ml: 1 }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            it is missing · click again to clear
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1.5 }}>
                        Applied to the whole result on Apply, so the column percentages move with these.
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />

                    <Grid container spacing={2}>
                        {GROUPS.map(group => (
                            <Grid item xs={6} key={group.title}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#6b7280', fontWeight: 700, display: 'block', mb: 0.5 }}
                                >
                                    {group.title}
                                </Typography>
                                {group.fields.map(field => (
                                    <FlagRow
                                        key={field.key}
                                        field={field}
                                        value={draft[field.key]}
                                        onChange={(value) => setFlag(field.key, value)}
                                    />
                                ))}
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Popover>
        </>
    )
}
